import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { FileText, Target, MessageSquare, Calendar, Clock, MapPin, Brain, UserPlus, Bell, CheckCircle, X, Eye } from "lucide-react";
import Navigation from "../components/Navigation";
import MVPSessionPlayer, { MVPSessionCard } from "../components/MVPSession";
import ConsultantRequestButton from "../components/ConsultantRequestButton";
import TeamJoinRequests from "../components/TeamJoinRequests";
import InviteUsers from "../components/InviteUsers";
import UserStorageLibrary from "../components/UserStorageLibrary";
import LinkifyText from "../components/LinkifyText";
import { WeeklyCompletionTracker } from "../components/plans/WeeklyCompletionTracker";
import WellnessRadarProfile from "../components/wellness/WellnessRadarProfile";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, onLogout }) {
  const [stats, setStats] = useState({
    activeTrainingPlans: 0,
    goalsActive: 0
  });
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [inspirationalQuote, setInspirationalQuote] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showStorageLibrary, setShowStorageLibrary] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [sendingReport, setSendingReport] = useState(false);
  const [individualTrends, setIndividualTrends] = useState([]);
  const [reportPreview, setReportPreview] = useState(null);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [planCompletions, setPlanCompletions] = useState({});
  const [mvpSession, setMvpSession] = useState(null);
  const [showMvpPlayer, setShowMvpPlayer] = useState(false);
  const navigate = useNavigate();

  const fetchIndividualTrends = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/wellness/trends/individual?days=30`, { headers });
      setIndividualTrends(response.data || []);
    } catch (error) {
      console.error("Failed to fetch individual trends:", error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchUpcomingEvents();
    fetchInspirationalQuote();
    fetchNotifications();
    fetchIndividualTrends();
    fetchTrainingPlans();
    
    // Set up polling for notifications every 60 seconds
    const notificationInterval = setInterval(fetchNotifications, 60000);
    
    return () => {
      clearInterval(notificationInterval);
    };
  }, []);

  const fetchTrainingPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/ai/plans`, { headers });
      const plans = response.data || [];
      setTrainingPlans(plans);

      const today = new Date();
      const dayOfWeek = today.getDay();
      const daysToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);
      const monday = new Date(today);
      monday.setDate(today.getDate() + daysToMonday);
      monday.setHours(0, 0, 0, 0);
      const weekStart = monday.toISOString().split('T')[0];

      for (const plan of plans) {
        try {
          const compRes = await axios.get(`${API}/mental-training/plan-weekly-completion/${plan.id}?week_start_date=${weekStart}`, { headers });
          setPlanCompletions(prev => ({
            ...prev,
            [plan.id]: {
              completed_days: compRes.data.completed_days || [],
              week_start_date: weekStart,
              plan_id: plan.id
            }
          }));
        } catch (err) {
          // ignore individual plan fetch errors
        }
      }
    } catch (error) {
      console.error("Failed to fetch training plans:", error);
    }
  };

  const sendWeeklyReport = async () => {
    setSendingReport(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai/weekly-progress-report`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        toast.success('Weekly progress report sent to your email!');
      } else if (response.data.status === 'preview') {
        setReportPreview(response.data);
        toast.info('Report preview generated (email not configured)');
      }
    } catch (error) {
      console.error('Failed to send weekly report:', error);
      toast.error('Failed to send weekly report');
    } finally {
      setSendingReport(false);
    }
  };

  const fetchInspirationalQuote = async () => {
    try {
      const response = await axios.get(`${API}/quotes/inspirational?featured_only=true&theme=perseverance`);
      if (response.data.length > 0) {
        // Get a random featured quote
        const randomIndex = Math.floor(Math.random() * response.data.length);
        setInspirationalQuote(response.data[randomIndex]);
      }
    } catch (error) {
      console.error("Failed to fetch inspirational quote:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Calculate date 30 days ago
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoISO = thirtyDaysAgo.toISOString();

      const [sessions, goals, metrics, plans] = await Promise.all([
        axios.get(`${API}/training/sessions`, { headers }),
        axios.get(`${API}/goals`, { headers }),
        axios.get(`${API}/performance/metrics?days=30`, { headers }),
        axios.get(`${API}/ai/plans`, { headers })
      ]);

      // Filter sessions completed in last 30 days
      const recentSessions = sessions.data.filter(session => {
        const sessionDate = new Date(session.completed_at || session.date);
        return sessionDate >= thirtyDaysAgo;
      });

      // Filter active goals
      const recentActiveGoals = goals.data.filter(g => g.status === 'active');

      // Count active training plans (plans created in last 30 days or with recent activity)
      const activePlans = plans.data.filter(plan => {
        const planDate = new Date(plan.created_at);
        return planDate >= thirtyDaysAgo;
      });

      setStats({
        activeTrainingPlans: activePlans.length,
        goalsActive: recentActiveGoals.length
      });
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/calendar/events/upcoming?limit=5`, { headers });
      setUpcomingEvents(response.data);
    } catch (error) {
      console.error("Failed to fetch calendar events:", error);
      // Don't show error toast on dashboard, calendar might not be connected yet
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/notifications?limit=10`, { headers });
      setNotifications(response.data || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setNotifications([]);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { headers });
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/notifications/${notificationId}`, { headers });
      
      // Update local state
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      toast.success("Notification deleted");
    } catch (error) {
      console.error("Failed to delete notification:", error);
      toast.error("Failed to delete notification");
    }
  };

  // Note: Health data sync removed - Whoop and Apple Watch integrations have been removed

  const location = useLocation();

  // Handle deep-link actions from the top-nav More menu (Storage modal, Weekly Report action)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('storage') === '1') {
      setShowStorageLibrary(true);
    }
    if (params.get('weeklyReport') === '1') {
      sendWeeklyReport();
    }
    if (params.get('storage') || params.get('weeklyReport')) {
      // Clean the URL so the action doesn't retrigger on refresh
      navigate('/dashboard', { replace: true });
    }
  }, [location.search]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
        <div className="mb-6 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
            Welcome back, {user.name}!
          </h1>
          <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400">
            Track your progress and continue building mental strength
          </p>
        </div>

        {/* Daily MVP Session */}
        {user.role === 'athlete' && (
          <div className="mb-6 sm:mb-8">
            <MVPSessionCard onStart={async () => {
              try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API}/mvp-session`, { headers: { Authorization: `Bearer ${token}` } });
                setMvpSession(res.data);
                setShowMvpPlayer(true);
              } catch { toast.error("Failed to load MVP session"); }
            }} />
          </div>
        )}

        {/* Stats Overview - Last 30 Days */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card 
            className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-indigo-100 cursor-pointer hover:shadow-lg active:scale-95 transition-all duration-300"
            onClick={() => navigate('/plans')}
            data-testid="active-plans-card"
          >
            <CardHeader className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl sm:text-2xl font-bold text-purple-600">
                    {stats.activeTrainingPlans}
                  </CardTitle>
                  <CardDescription className="text-purple-700 font-medium text-xs sm:text-sm">
                    Active Training Plans
                  </CardDescription>
                </div>
                <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
              </div>
              <p className="text-[10px] sm:text-xs text-purple-500 mt-1 sm:mt-2">Last 30 days</p>
            </CardHeader>
          </Card>

          <Card 
            className="border-0 shadow-md bg-gradient-to-br from-teal-50 to-blue-100 cursor-pointer hover:shadow-lg active:scale-95 transition-all duration-300"
            onClick={() => navigate('/goals')}
            data-testid="active-goals-card"
          >
            <CardHeader className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl sm:text-2xl font-bold text-teal-600">
                    {stats.goalsActive}
                  </CardTitle>
                  <CardDescription className="text-teal-700 font-medium text-xs sm:text-sm">
                    Active Goals
                  </CardDescription>
                </div>
                <Target className="w-6 h-6 sm:w-8 sm:h-8 text-teal-400" />
              </div>
              <p className="text-[10px] sm:text-xs text-teal-500 mt-1 sm:mt-2">Last 30 days</p>
            </CardHeader>
          </Card>
        </div>

        {/* Mental Performance Profile + Weekly Completion */}
        {individualTrends.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8" data-testid="performance-profile-section">
            {/* Weekly Mental Training Completion */}
            <div data-testid="dashboard-weekly-completion">
              <WeeklyCompletionTracker
                trainingPlans={trainingPlans}
                planCompletions={planCompletions}
              />
            </div>

            {/* My Mental Performance Profile: Radar + Metrics combined */}
            <div data-testid="mental-performance-profile" className="bg-white rounded-xl shadow-lg p-5 border border-emerald-100">
              <h2 className="text-base font-bold text-gray-900 mb-3">My Mental Performance Profile</h2>
              <div className="grid grid-cols-2 gap-3">
                {/* Radar chart left */}
                <div className="flex flex-col items-center">
                  {(() => {
                    const recent = individualTrends.slice(-7);
                    const avg = (k) => recent.reduce((s, d) => s + (d[k] || 0), 0) / recent.length;
                    const rd = [
                      { metric: 'Mood', value: +avg('mood').toFixed(1), fullMark: 10 },
                      { metric: 'Energy', value: +avg('energy').toFixed(1), fullMark: 10 },
                      { metric: 'Confidence', value: +avg('confidence').toFixed(1), fullMark: 10 },
                      { metric: 'Focus', value: +avg('focus').toFixed(1), fullMark: 10 },
                      { metric: 'Sleep', value: +avg('sleep_quality').toFixed(1), fullMark: 10 },
                      { metric: 'Performance', value: +avg('performance_rating').toFixed(1), fullMark: 10 },
                      { metric: 'Low Stress', value: +(10 - avg('stress')).toFixed(1), fullMark: 10 },
                    ];
                    const score = (rd.reduce((s, d) => s + d.value, 0) / rd.length).toFixed(1);
                    return (
                      <>
                        <div className="text-center mb-1">
                          <span className="text-lg font-bold text-emerald-600">{score}</span>
                          <span className="text-xs text-gray-500">/10</span>
                        </div>
                        <ResponsiveContainer width="100%" height={180}>
                          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={rd}>
                            <PolarGrid stroke="#d1d5db" />
                            <PolarAngleAxis dataKey="metric" tick={{ fontSize: 8, fill: '#374151', fontWeight: 600 }} />
                            <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} />
                            <Radar dataKey="value" stroke="#059669" fill="#10b981" fillOpacity={0.25} strokeWidth={2} dot={{ r: 2.5, fill: '#059669' }} />
                          </RadarChart>
                        </ResponsiveContainer>
                      </>
                    );
                  })()}
                </div>

                {/* Metrics with trends right */}
                <div>
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-2">7-Day Averages</p>
                  <div className="space-y-1.5">
                    {[
                      { key: 'mood', label: 'Mood', color: 'text-yellow-500' },
                      { key: 'stress', label: 'Stress', color: 'text-red-500' },
                      { key: 'energy', label: 'Energy', color: 'text-green-500' },
                      { key: 'confidence', label: 'Confidence', color: 'text-blue-500' },
                      { key: 'sleep_quality', label: 'Sleep', color: 'text-purple-500' },
                      { key: 'performance_rating', label: 'Performance', color: 'text-orange-500' },
                    ].map(({ key, label, color }) => {
                      const recent = individualTrends.slice(-7);
                      const prev = individualTrends.slice(-14, -7);
                      const avg = recent.reduce((s, d) => s + (d[key] || 0), 0) / (recent.length || 1);
                      const prevAvg = prev.length > 0 ? prev.reduce((s, d) => s + (d[key] || 0), 0) / prev.length : null;
                      const diff = prevAvg !== null ? avg - prevAvg : null;
                      const isStress = key === 'stress';
                      const improved = diff !== null && (isStress ? diff < -0.3 : diff > 0.3);
                      const declined = diff !== null && (isStress ? diff > 0.3 : diff < -0.3);
                      return (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-500 font-medium w-20">{label}</span>
                          <span className={`text-sm font-bold ${color}`}>{avg.toFixed(1)}</span>
                          {diff !== null && (
                            <span className={`text-[9px] font-bold w-10 text-right ${improved ? 'text-emerald-500' : declined ? 'text-rose-500' : 'text-gray-400'}`}>
                              {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Join Requests (for coaches and team admins) */}
        {user && ['coach', 'team-admin', 'system-admin'].includes(user.role) && (
          <div className="mb-12">
            <TeamJoinRequests user={user} />
          </div>
        )}

        {/* Calendar Widget */}
        {upcomingEvents.length > 0 && (
          <div className="mb-6 sm:mb-12">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Upcoming Events</h2>
              <Button
                onClick={() => navigate('/schedule')}
                variant="outline"
                size="sm"
                className="text-emerald-600 border-emerald-600 hover:bg-emerald-50 text-xs sm:text-sm"
              >
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                View All
              </Button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              {upcomingEvents.slice(0, 3).map((event) => {
                const startDate = new Date(event.start_datetime);
                return (
                  <Card
                    key={event.id}
                    onClick={() => navigate('/schedule')}
                    className="border-0 shadow-sm bg-white/90 hover:shadow-md transition-all duration-200 cursor-pointer active:scale-[0.98]"
                    data-testid={`event-card-${event.id}`}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                          event.event_type === 'practice' ? 'bg-blue-100 text-blue-700' :
                          event.event_type === 'game' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {event.event_type}
                        </div>
                        {event.opponent && (
                          <span className="text-xs font-medium text-orange-600 truncate">vs {event.opponent}</span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-900 truncate">{event.title}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        {event.location && (
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3" />
                            {event.location}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Notifications Section */}
        <div className="mt-8 sm:mt-16 mb-6 sm:mb-12">
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-8 flex items-center gap-2 sm:gap-3">
            <Bell className="w-5 h-5 sm:w-8 sm:h-8 text-indigo-600" />
            Notifications
          </h2>
          
          <div className="space-y-3 sm:space-y-4">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <Card key={notification.id} className={`border-0 shadow-lg transition-all duration-300 ${
                  !notification.read ? 'bg-gradient-to-r from-indigo-50 to-blue-50 border-l-4 border-indigo-500' : 'bg-white'
                }`}>
                  <CardContent className="p-3 sm:p-6">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 sm:gap-4 flex-1 min-w-0">
                        <div className={`p-2 rounded-full ${
                          notification.type === 'success' || notification.type === 'goal' ? 'bg-green-100' :
                          notification.type === 'message' ? 'bg-blue-100' :
                          notification.type === 'training_plan' ? 'bg-purple-100' :
                          notification.type === 'schedule' ? 'bg-orange-100' : 'bg-gray-100'
                        }`}>
                          {notification.type === 'success' || notification.type === 'goal' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : notification.type === 'message' ? (
                            <MessageSquare className="w-5 h-5 text-blue-600" />
                          ) : notification.type === 'training_plan' ? (
                            <Brain className="w-5 h-5 text-purple-600" />
                          ) : notification.type === 'schedule' ? (
                            <Calendar className="w-5 h-5 text-orange-600" />
                          ) : (
                            <Bell className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => {
                            if (!notification.read) {
                              markNotificationAsRead(notification.id);
                            }
                          }}
                        >
                          <h3 className={`font-semibold text-sm sm:text-lg mb-1 sm:mb-2 ${!notification.read ? 'text-indigo-900' : 'text-gray-900'}`}>
                            {notification.title}
                          </h3>
                          <LinkifyText className={`${!notification.read ? 'text-indigo-700' : 'text-gray-600'} mb-2`}>
                            {notification.message}
                          </LinkifyText>
                          <p className="text-sm text-gray-500">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-2 ml-4">
                        {!notification.read && (
                          <div className="w-3 h-3 bg-indigo-500 rounded-full flex-shrink-0 mt-2"></div>
                        )}
                        <div className="flex flex-col gap-2">
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                markNotificationAsRead(notification.id);
                              }}
                              className="h-8 w-8 p-0 text-indigo-600 hover:bg-indigo-100"
                              title="Mark as read"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                            title="Delete notification"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="border-0 shadow-lg bg-white">
                <CardContent className="p-12 text-center">
                  <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No notifications</h3>
                  <p className="text-gray-500">You&apos;re all caught up! We&apos;ll notify you of important updates.</p>
                </CardContent>
              </Card>
            )}
          </div>
          
          {notifications.length > 0 && (
            <div className="text-center mt-6">
              <Button
                variant="outline"
                onClick={() => navigate('/notifications')}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                <Bell className="w-4 h-4 mr-2" />
                View All Notifications
              </Button>
            </div>
          )}
        </div>

        {/* Invite Friends Section - Bottom of Page */}
        <div className="mt-16 mb-12">
          <div className="max-w-2xl mx-auto">
            <Card className="border-0 shadow-2xl bg-gradient-to-br from-emerald-100 via-teal-100 to-green-100">
              <CardContent className="p-12 text-center">
                <div className="mb-8">
                  <UserPlus className="w-20 h-20 text-emerald-600 mx-auto mb-6" />
                  <h2 className="text-3xl font-bold text-emerald-900 mb-4">
                    Invite Your Friends
                  </h2>
                  <p className="text-lg text-emerald-800 mb-8 leading-relaxed">
                    Share PeakMind with your teammates, training partners, and friends. 
                    Help them unlock their mental potential and achieve peak performance together.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <Button
                    onClick={() => setShowInviteDialog(true)}
                    size="lg"
                    className="w-full max-w-md bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <UserPlus className="w-6 h-6 mr-3" />
                    Send Invitations
                  </Button>
                  
                  <p className="text-sm text-emerald-700">
                    Invite via email or share your referral link
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Invite Users Dialog */}
      <InviteUsers 
        isOpen={showInviteDialog} 
        onClose={() => setShowInviteDialog(false)} 
      />
      
      {/* User Storage Library Dialog */}
      {showStorageLibrary && (
        <UserStorageLibrary 
          onClose={() => setShowStorageLibrary(false)} 
        />
      )}

      {/* MVP Session Full-Screen Player */}
      {showMvpPlayer && mvpSession && (
        <MVPSessionPlayer
          session={mvpSession}
          onComplete={() => {}}
          onClose={() => setShowMvpPlayer(false)}
        />
      )}
    </div>
  );
}